import { useEffect, useReducer, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Users, GitBranch, BarChart2, Building2 } from "lucide-react";
import { toast } from "sonner";
import ImportTab from "./ImportTab";
import ProfilesTab from "./ProfilesTab";
import CompetencyMapTab from "./CompetencyMapTab";
import GapAnalysisTab from "./GapAnalysisTab";
import ReconciliationTab from "./ReconciliationTab";
import type { HubState, WorkspaceCourse, LearnerProfile, Taxonomy, IdentifiedGap } from "@/lib/learnerHub/types";
import { CCM_TAXONOMY, ROLE_PROFILES } from "@/lib/learnerHub/constants";

interface Props {
  workspaceId: string;
  courses: WorkspaceCourse[];
  taxonomyLibrary: Taxonomy[];
  importProfile?: LearnerProfile | null;
  requestedSubTab?: HubState["activeTab"] | null;
  onSubTabConsumed?: () => void;
  onLearnersChange?: (count: number) => void;
  onGapsComputed?: (gaps: IdentifiedGap[]) => void;
  onGenerateCourse?: (learner: LearnerProfile, gapUnitIds: string[], targetRole: string) => void;
}

const STORAGE_KEY = (wid: string) => `learner_hub_${wid}`;

function loadPersisted(workspaceId: string): Partial<HubState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(workspaceId));
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function makeInitialState(workspaceId: string): HubState {
  const saved = loadPersisted(workspaceId);
  return {
    activeTab: saved.activeTab ?? "import",
    importTier: "tier2",
    learners: saved.learners ?? [],
    taxonomy: saved.taxonomy ?? CCM_TAXONOMY,
    csvData: null,
    csvMapping: null,
    csvWorking: false,
    talentDomain: "",
    talentKey: "",
    talentPreview: null,
    talentWorking: false,
    selectedLearnerId: saved.selectedLearnerId ?? null,
    showDemoCourse: false,
    selectedRole: saved.selectedRole ?? Object.keys(ROLE_PROFILES)[0],
    expandedLearnerId: saved.expandedLearnerId ?? null,
    toast: null,
  };
}

function reducer(state: HubState, partial: Partial<HubState>): HubState {
  return { ...state, ...partial };
}

export default function WorkspaceLearnerHub({
  workspaceId,
  courses,
  taxonomyLibrary,
  importProfile,
  requestedSubTab,
  onSubTabConsumed,
  onLearnersChange,
  onGapsComputed,
  onGenerateCourse,
}: Props) {
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => makeInitialState(workspaceId)
  );

  const setHub = useCallback((partial: Partial<HubState>) => dispatch(partial), []);

  // Persist learner data and key UI state per workspace
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY(workspaceId),
        JSON.stringify({
          learners: state.learners,
          activeTab: state.activeTab,
          selectedLearnerId: state.selectedLearnerId,
          selectedRole: state.selectedRole,
          expandedLearnerId: state.expandedLearnerId,
          taxonomy: state.taxonomy,
        })
      );
    } catch {}
  }, [
    workspaceId,
    state.learners,
    state.activeTab,
    state.selectedLearnerId,
    state.selectedRole,
    state.expandedLearnerId,
    state.taxonomy,
  ]);

  // Fire toasts
  useEffect(() => {
    if (!state.toast) return;
    if (state.toast.type === "success") {
      toast.success(state.toast.message, { duration: 3500 });
    } else {
      toast.error(state.toast.message, { duration: 3500 });
    }
    dispatch({ toast: null });
  }, [state.toast]);

  // Ingest a profile exported from the individual assessment tab
  useEffect(() => {
    if (!importProfile) return;
    setHub({
      learners: [...state.learners, importProfile],
      activeTab: "profiles",
      toast: { message: `${importProfile.name} added to learner profiles`, type: "success" },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importProfile]);

  // Apply sub-tab navigation requested by parent (demo rail)
  useEffect(() => {
    if (!requestedSubTab) return;
    setHub({ activeTab: requestedSubTab });
    onSubTabConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSubTab]);

  // Notify parent when learner count changes (demo step completion)
  useEffect(() => {
    onLearnersChange?.(state.learners.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.learners.length]);

  const TAB_ITEMS = [
    { value: "import" as const, label: "Import", icon: Upload },
    { value: "profiles" as const, label: `Profiles (${state.learners.length})`, icon: Users },
    { value: "competency" as const, label: "Competency map", icon: GitBranch },
    { value: "gap" as const, label: "Gap analysis", icon: BarChart2 },
    { value: "reconcile" as const, label: "Cross-site reconciliation", icon: Building2 },
  ];

  const updateLearner = useCallback(
    (updated: LearnerProfile) => {
      setHub({
        learners: state.learners.map((l) => (l.id === updated.id ? updated : l)),
      });
    },
    [state.learners, setHub]
  );

  return (
    <Tabs
      value={state.activeTab}
      onValueChange={(v) => setHub({ activeTab: v as HubState["activeTab"] })}
    >
      <TabsList className="mb-6 flex-wrap h-auto gap-1">
        {TAB_ITEMS.map(({ value, label, icon: Icon }) => (
          <TabsTrigger key={value} value={value} className="flex items-center gap-1.5 text-sm">
            <Icon className="h-4 w-4" />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="import">
        <ImportTab state={state} setHub={setHub} />
      </TabsContent>

      <TabsContent value="profiles">
        <ProfilesTab
          state={state}
          setHub={setHub}
          courses={courses}
          onUpdateLearner={updateLearner}
        />
      </TabsContent>

      <TabsContent value="competency">
        <CompetencyMapTab state={state} setHub={setHub} taxonomyLibrary={taxonomyLibrary} />
      </TabsContent>

      <TabsContent value="gap">
        <GapAnalysisTab
          state={state}
          setHub={setHub}
          taxonomyLibrary={taxonomyLibrary}
          onGenerateCourse={onGenerateCourse}
          onGapsComputed={onGapsComputed}
        />
      </TabsContent>

      <TabsContent value="reconcile">
        <ReconciliationTab
          onExport={(profiles) =>
            setHub({
              learners: profiles,
              activeTab: "profiles",
              toast: {
                message: `${profiles.length} learner${profiles.length !== 1 ? "s" : ""} imported from reconciliation`,
                type: "success",
              },
            })
          }
          onViewProfile={(employeeId) => {
            const learner = state.learners.find((l) => l.employeeId === employeeId);
            setHub({
              activeTab: "profiles",
              ...(learner ? { expandedLearnerId: learner.id } : {}),
            });
          }}
        />
      </TabsContent>
    </Tabs>
  );
}
